import { useCallback, useEffect, useRef, useState } from "react";
import { useRequestConfig } from "./RequestConfig";
const REQUEST_STATUS = {
    IDLE: "idle",
    LOADING: "loading",
    SUCCESS: "success",
    ERROR: "error",
    CANCELLED: "cancelled",
};
export function useRequest(fetcher, options = {
    initialParams: undefined,
    requestOnMount: false,
    suppressError: false,
    onError: undefined,
    propagateError: false,
}) {
    const globalConfig = useRequestConfig();
    const [status, setStatus] = useState(REQUEST_STATUS.IDLE);
    const abortControllerRef = useRef(null);
    const fetcherRef = useRef(fetcher);
    const optionsRef = useRef(options);
    useEffect(() => {
        fetcherRef.current = fetcher;
        optionsRef.current = options;
    }, [fetcher, options]);
    const request = useCallback(async (payload, callOptions) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const signal = controller.signal;
        setStatus(REQUEST_STATUS.LOADING);
        try {
            const result = await fetcherRef.current(payload, { signal });
            setStatus(REQUEST_STATUS.SUCCESS);
            callOptions?.onSuccess?.(result);
            return result;
        }
        catch (error) {
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            if (error?.name === "AbortError") {
                setStatus(REQUEST_STATUS.CANCELLED);
                return;
            }
            setStatus(REQUEST_STATUS.ERROR);
            if (import.meta.env.DEV) {
                console.error(error);
            }
            callOptions?.onFail?.(error);
            if (optionsRef.current.suppressError) {
                return;
            }
            if (optionsRef.current.onError) {
                optionsRef.current.onError?.(error, {
                    triggerGlobalNotifyError: () => globalConfig?.onError?.(error),
                });
            }
            else {
                globalConfig?.onError?.(error);
            }
            if (optionsRef.current.propagateError) {
                throw error;
            }
        }
    }, [globalConfig]);
    useEffect(() => {
        if (optionsRef.current.requestOnMount) {
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            request(optionsRef.current.initialParams);
        }
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [request]);
    return {
        status: {
            isPristine: status === REQUEST_STATUS.IDLE,
            isSuccess: status === REQUEST_STATUS.SUCCESS,
            isLoading: status === REQUEST_STATUS.LOADING,
            isError: status === REQUEST_STATUS.ERROR,
            isCancel: status === REQUEST_STATUS.CANCELLED,
        },
        request,
        abort: () => abortControllerRef.current?.abort(),
    };
}
